// Inline message-body formatter. Supports the WhatsApp/Slack-flavored
// minimal markdown:
//
//   *bold*           → <strong>bold</strong>
//   _italic_         → <em>italic</em>
//   ~strike~         → <s>strike</s>
//   `code`           → <code>code</code>
//   @username        → clickable mention (when a known participant)
//
// Plus autolinking of http(s) URLs to clickable anchors.
//
// Deliberately *not* a full markdown engine — we don't want links inside
// bold or nested formatting weirdness, and we want the raw asterisks to
// fall through unchanged when they don't form a valid pair (e.g. a
// "5 * 3 = 15" math line shouldn't render as italic-broken text).
//
// React's text nodes auto-escape, so plain string segments are safe.
// Anchor hrefs are validated against the URL constructor to keep
// `javascript:` / `data:` URIs out — only http(s)/mailto/tel pass through.

import type { ReactNode } from 'react';

// Caller resolves an @handle string (no leading @) → user info, or null
// when the handle doesn't match a known participant. The formatter uses
// the resolver per-mention so the conversation's full participant list
// only needs to be walked once outside the formatter.
export interface MentionContext {
  resolveHandle: (handle: string) => { id: string; isMe: boolean } | null;
  // Click handler for resolved mentions — typically opens a DM with the
  // mentioned user. Receives the resolved user id.
  onMentionClick?: (userId: string) => void;
}

const URL_RE = /\bhttps?:\/\/[^\s<>]+/gi;
// @-mention pattern: same character class the composer uses for picking
// a mention (handleFor allows letters/digits/dot/dash/underscore in
// usernames, plus we accept email local-parts as fallback handles).
// We match the @-prefix only when at start of string OR after whitespace
// to avoid false-positives like "you@example.com".
const MENTION_RE = /(^|\s)@([A-Za-z0-9_.\-]+)/g;
// One regex with four alternation groups so a single matchAll pass can
// route to the right node type without re-walking the string per style.
// Each pattern caps at "no newline / no nested same-delimiter" so we
// don't cross paragraph boundaries.
const INLINE_RE = /\*([^*\n]+)\*|_([^_\n]+)_|~([^~\n]+)~|`([^`\n]+)`/g;

function safeUrl(raw: string): string | null {
  try {
    const u = new URL(raw);
    // Allow http/https only — mailto/tel could theoretically be useful
    // but they're rare in chat and the validation overhead doesn't
    // justify it for an MVP. Any other scheme is dropped to plain text.
    if (u.protocol === 'http:' || u.protocol === 'https:') return raw;
    return null;
  } catch {
    return null;
  }
}

// Trailing punctuation that's commonly typed after a URL but isn't part
// of it ("Check out https://example.com." / "...example.com)"). We strip
// these, render them as plain text after the link, and don't include
// them in the href.
function trimTrailingPunct(raw: string): { url: string; trail: string } {
  const m = raw.match(/[).,!?;:]+$/);
  if (!m) return { url: raw, trail: '' };
  // Be careful with closing parens — only trim if there's no opening
  // paren in the URL, otherwise we'd break links like
  // https://en.wikipedia.org/wiki/Foo_(bar).
  const trail = m[0]!;
  const stripped = raw.slice(0, raw.length - trail.length);
  if (trail.includes(')') && stripped.includes('(') && !stripped.includes(')')) {
    return { url: raw, trail: '' };
  }
  return { url: stripped, trail };
}

function formatInline(
  text: string,
  keyBase: string,
  mentions?: MentionContext,
): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(INLINE_RE)) {
    const idx = m.index!;
    if (idx > last) {
      out.push(...formatMentionsAndText(text.slice(last, idx), `${keyBase}-mb${i}`, mentions));
    }
    const k = `${keyBase}-i${i++}`;
    if (m[1] !== undefined) out.push(<strong key={k}>{m[1]}</strong>);
    else if (m[2] !== undefined) out.push(<em key={k}>{m[2]}</em>);
    else if (m[3] !== undefined) out.push(<s key={k}>{m[3]}</s>);
    else if (m[4] !== undefined)
      out.push(
        <code
          key={k}
          className="px-1 py-0.5 rounded bg-black/20 font-mono text-[0.85em]"
        >
          {m[4]}
        </code>,
      );
    last = idx + m[0].length;
  }
  if (last < text.length) {
    out.push(...formatMentionsAndText(text.slice(last), `${keyBase}-tail`, mentions));
  }
  return out;
}

// Inner pass that scans plain text for @-mentions. Anything that doesn't
// look like a mention OR doesn't resolve to a known participant falls
// through as plain text.
function formatMentionsAndText(
  text: string,
  keyBase: string,
  mentions: MentionContext | undefined,
): ReactNode[] {
  if (!mentions) return [text];
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  // Reset the regex's lastIndex by creating a fresh one — we use the
  // global flag for matchAll but lastIndex persists across iterations
  // when reused. Cloning is safer.
  const re = new RegExp(MENTION_RE.source, 'g');
  for (const m of text.matchAll(re)) {
    const handle = m[2]!;
    const resolved = mentions.resolveHandle(handle);
    if (!resolved) continue;
    // m.index points to the leading whitespace (if any). The actual @
    // is at m.index + m[1].length.
    const atIdx = m.index! + (m[1] ?? '').length;
    if (atIdx > last) out.push(text.slice(last, atIdx));
    out.push(
      <button
        key={`${keyBase}-m${i++}`}
        type="button"
        onClick={() => mentions.onMentionClick?.(resolved.id)}
        className={`inline rounded px-1 -mx-0.5 font-medium transition-colors ${
          resolved.isMe
            ? 'bg-amber-500/20 text-amber-200 hover:bg-amber-500/30'
            : 'bg-indigo-500/15 text-indigo-200 hover:bg-indigo-500/25'
        }`}
      >
        @{handle}
      </button>,
    );
    last = atIdx + 1 + handle.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function formatMessageBody(
  body: string | null | undefined,
  mentions?: MentionContext,
): ReactNode[] {
  if (!body) return [];
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of body.matchAll(URL_RE)) {
    const idx = m.index!;
    if (idx > last) {
      out.push(...formatInline(body.slice(last, idx), `pre${i}`, mentions));
    }
    const { url, trail } = trimTrailingPunct(m[0]);
    const safe = safeUrl(url);
    if (safe) {
      out.push(
        <a
          key={`u${i}`}
          href={safe}
          target="_blank"
          rel="noopener noreferrer"
          className="underline decoration-1 underline-offset-2 hover:opacity-80"
        >
          {url}
        </a>,
      );
      if (trail) out.push(trail);
    } else {
      // Couldn't safely parse — fall through as plain text so the user
      // still sees their original characters.
      out.push(m[0]);
    }
    last = idx + m[0].length;
    i++;
  }
  if (last < body.length) {
    out.push(...formatInline(body.slice(last), `tail`, mentions));
  }
  return out;
}
